import { faClock } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CalendarDateTime } from '@internationalized/date';
import { FormControl, View } from '@zinc/ui';
import moment from 'moment';
import { DateInput, DateSegment, I18nProvider, TimeField } from 'react-aria-components';

interface TimePickerProps {
  value: string | Date | moment.Moment | null | undefined; // Allow moment input
  label: string;
  onChange?: (newValue: Date) => void;
  error?: boolean;
  hourCycle?: 12 | 24;
}

const TimePicker: React.FC<TimePickerProps> = ({ value, label, onChange, error, hourCycle }) => {
  // Convert the string or Date prop to a Date object
  const dateValue = (() => {
    if (!value) return undefined;
    if (value instanceof Date) return value;
    if (typeof value === 'string') return new Date(value);
    if (moment.isMoment(value)) return value.toDate();
    return value as Date | null;
  })();

  // Convert the Date object to a CalendarDateTime for TimeField
  const preparedTimeValue = dateValue
    ? new CalendarDateTime(
        dateValue.getFullYear(),
        dateValue.getMonth() + 1,
        dateValue.getDate(),
        dateValue.getHours(),
        dateValue.getMinutes()
      )
    : null;

  // Update only the time part of the Date on change
  const handleTimeChange = (newTime: CalendarDateTime) => {
    if (!dateValue) return;

    const updatedDateTime = new Date(dateValue);
    updatedDateTime.setHours(newTime.hour);
    updatedDateTime.setMinutes(newTime.minute);
    updatedDateTime.setSeconds(0, 0);

    onChange?.(updatedDateTime);
  };

  return (
    <I18nProvider locale={window.localStorage.locale}>
      <TimeField
        aria-labelledby="time-picker-label"
        value={preparedTimeValue}
        onChange={handleTimeChange}
        hourCycle={hourCycle}
      >
        <FormControl>
          <FormControl.Label id="time-picker-label">{label}</FormControl.Label>
          <View
            borderRadius="small"
            direction="row"
            gap={5}
            align="center"
            padding={2}
            borderColor={error ? 'critical' : 'neutral-faded'}
            justify="space-between"
          >
            <DateInput className="flex flex-row">
              {(segment) => <DateSegment segment={segment} />}
            </DateInput>
          </View>
        </FormControl>
      </TimeField>
    </I18nProvider>
  );
};

export default TimePicker;
